import { Link } from 'react-router-dom'
import { Footer } from '../components/Footer'
import { useT } from '../i18n/context'
import { useAuthStore } from '../stores/auth'

const para =
    'mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300'
const list =
    'mt-2 list-disc space-y-1 pl-5 text-sm leading-6 text-slate-600 dark:text-slate-300'
const mailto =
    'font-medium text-indigo-700 dark:text-indigo-400 hover:underline'

/** Rahmen aller Rechtsseiten: ohne Login erreichbar, mit Footer. */
function LegalPage({
    title,
    children,
}: {
    title: string
    children: React.ReactNode
}) {
    const t = useT()
    const authenticated =
        useAuthStore(s => s.status) === 'authenticated'

    return (
        <div className='flex min-h-screen flex-col bg-gradient-to-b from-indigo-50 to-slate-100 dark:from-slate-900 dark:to-slate-950'>
            <main className='mx-auto w-full max-w-3xl flex-1 px-4 py-10'>
                <Link
                    to={authenticated ? '/' : '/login'}
                    className='text-sm font-medium text-indigo-700 dark:text-indigo-400 hover:underline'
                >
                    {t('common.back')}
                </Link>
                <h1 className='mt-4 text-2xl font-bold tracking-tight text-slate-800 dark:text-slate-100'>
                    {title}
                </h1>
                <p className='mt-1 text-sm text-slate-500 dark:text-slate-400'>
                    {t('legal.noteTemplate')}
                </p>
                <p
                    role='note'
                    className='mt-3 rounded-lg border border-amber-300/60 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/40 px-4 py-3 text-sm text-amber-800 dark:text-amber-200'
                >
                    {t('legal.noteBeta')}
                </p>
                <div className='mt-2'>{children}</div>
            </main>
            <Footer />
        </div>
    )
}

function Section({
    heading,
    children,
}: {
    heading: string
    children: React.ReactNode
}) {
    return (
        <section className='mt-8'>
            <h2 className='text-lg font-semibold text-slate-800 dark:text-slate-100'>
                {heading}
            </h2>
            {children}
        </section>
    )
}

/** Mehrzeilige i18n-Texte (\n) als Absätze rendern. */
function Lines({ text }: { text: string }) {
    return (
        <>
            {text.split('\n').map(line => (
                <p key={line} className={para}>
                    {line}
                </p>
            ))}
        </>
    )
}

export function ImpressumPage() {
    const t = useT()
    return (
        <LegalPage title={t('legal.impressum.title')}>
            <Section heading={t('legal.impressum.mediaholderH')}>
                <p className={para}>{t('legal.impressum.mediaholder')}</p>
                <Lines text={t('legal.impressum.address')} />
            </Section>
            <Section heading={t('legal.impressum.contactH')}>
                <p className={para}>
                    {t('legal.impressum.emailLabel')}:{' '}
                    <a href='mailto:hallo@optimustime.at' className={mailto}>
                        hallo@optimustime.at
                    </a>
                </p>
            </Section>
            <Section heading={t('legal.impressum.businessH')}>
                <ul className={list}>
                    <li>{t('legal.impressum.business')}</li>
                    <li>{t('legal.impressum.vat')}</li>
                    <li>{t('legal.impressum.authority')}</li>
                    <li>{t('legal.impressum.membership')}</li>
                </ul>
            </Section>
            <Section heading={t('legal.impressum.odrH')}>
                <p className={para}>{t('legal.impressum.odr')}</p>
            </Section>
        </LegalPage>
    )
}

export function DatenschutzPage() {
    const t = useT()
    return (
        <LegalPage title={t('legal.datenschutz.title')}>
            <Section heading={t('legal.datenschutz.controllerH')}>
                <Lines text={t('legal.datenschutz.controller')} />
                <p className={para}>{t('legal.datenschutz.controllerContact')}</p>
            </Section>
            <Section heading={t('legal.datenschutz.dataH')}>
                <p className={para}>{t('legal.datenschutz.dataIntro')}</p>
                <ul className={list}>
                    <li>{t('legal.datenschutz.dataAccount')}</li>
                    <li>{t('legal.datenschutz.dataTracking')}</li>
                    <li>{t('legal.datenschutz.dataTechnical')}</li>
                </ul>
            </Section>
            <Section heading={t('legal.datenschutz.cookiesH')}>
                <p className={para}>{t('legal.datenschutz.cookiesIntro')}</p>
                <ul className={list}>
                    <li>{t('legal.datenschutz.cookiesSession')}</li>
                    <li>{t('legal.datenschutz.cookiesLocale')}</li>
                    <li>{t('legal.datenschutz.cookiesStorage')}</li>
                    <li>{t('legal.datenschutz.cookiesFuture')}</li>
                </ul>
            </Section>
            <Section heading={t('legal.datenschutz.reachH')}>
                <p className={para}>{t('legal.datenschutz.reachText')}</p>
            </Section>
            <Section heading={t('legal.datenschutz.purposeH')}>
                <p className={para}>{t('legal.datenschutz.purposeText')}</p>
            </Section>
            <Section heading={t('legal.datenschutz.hostingH')}>
                <p className={para}>{t('legal.datenschutz.hostingText')}</p>
            </Section>
            <Section heading={t('legal.datenschutz.emailH')}>
                <p className={para}>{t('legal.datenschutz.emailText')}</p>
            </Section>
            <Section heading={t('legal.datenschutz.retentionH')}>
                <p className={para}>{t('legal.datenschutz.retentionText')}</p>
            </Section>
            <Section heading={t('legal.datenschutz.rightsH')}>
                <p className={para}>{t('legal.datenschutz.rightsText')}</p>
            </Section>
            <Section heading={t('legal.datenschutz.changesH')}>
                <p className={para}>{t('legal.datenschutz.changesText')}</p>
            </Section>
        </LegalPage>
    )
}

export function AgbPage() {
    const t = useT()
    const sections = [1, 2, 3, 4, 5, 6, 7, 8] as const
    return (
        <LegalPage title={t('legal.agb.title')}>
            {sections.map(n => (
                <Section
                    key={n}
                    heading={t(`legal.agb.s${n}H` as const)}
                >
                    <p className={para}>{t(`legal.agb.s${n}T` as const)}</p>
                </Section>
            ))}
        </LegalPage>
    )
}
